import { RRSPAccount_PlanType } from "../../generated/core/v1/accounts";
import type { Family } from "../../generated/core/v1/family";
import { addMonths, compareDates, type PDate } from "../lib/dates";
import type { Plan } from "./core";

/** One job's group-RRSP match: offer, rate, contributions by year. */
export type GroupRrspMatch = {
  jobUuid: string;
  firstName: string;
  employer: string;
  matchingRate: number;
  capRate: number;
  /** The rate in force today: the plan's latest yearly decision, else what the employee puts in now. */
  currentRate: number;
  /** The year the plan first decides a rate above 0, if any. */
  startsIn?: number;
  /** Each later change of the plan's rate (a yearly decision that differs from the one before). */
  changes: { year: number; rate: number }[];
  /** Every year from the first contribution to the last, years without any included (as 0). */
  years: { year: number; you: number; employer: number }[];
  totalYou: number;
  totalEmployer: number;
};

/** The plan's yearly group RRSP rate decisions for one job (made every year, changed or not). */
function rateDecisions(plan: NonNullable<Plan>, jobUuid: string) {
  return plan.decisions.flatMap((d) =>
    d.kind.oneofKind === "groupRrspRate" && d.kind.groupRrspRate.jobUuid === jobUuid
      ? [{ date: d.date!, rate: d.kind.groupRrspRate.employeeContributionRate }]
      : [],
  );
}

/** The group RRSP accounts: the family's (by type or link) and those the plan decides rates for. */
export function groupRrspUuids(
  family: Family | undefined | null,
  ...plans: (Plan | undefined | null)[]
): Set<string> {
  const out = new Set<string>();
  family?.persons.forEach((p) => {
    p.rrspAccounts.forEach((a) => a.planType === RRSPAccount_PlanType.GROUP && out.add(a.uuid));
    p.jobs.forEach((j) => j.groupRrspPlan?.groupRrspUuid && out.add(j.groupRrspPlan.groupRrspUuid));
  });
  for (const plan of plans)
    for (const d of plan?.decisions ?? [])
      if (d.kind.oneofKind === "groupRrspRate") out.add(d.kind.groupRrspRate.groupRrspUuid);
  out.delete("");
  return out;
}

export function groupRrspMatches(
  plan: Plan,
  family: Family | undefined,
  today: PDate,
): GroupRrspMatch[] {
  if (!plan || !family) return [];
  return family.persons.flatMap((p) =>
    p.jobs.flatMap((job) => {
      const offer = job.groupRrspPlan;
      if (!offer) return [];
      const sets = rateDecisions(plan, job.uuid);
      // the first decision can be dated just after today
      const current =
        sets.filter((s) => compareDates(s.date, today) <= 0).at(-1) ??
        (sets[0] && compareDates(sets[0].date, addMonths(today, 1)) <= 0 ? sets[0] : undefined);
      const byYear = new Map<number, { year: number; you: number; employer: number }>();
      for (const e of plan.events) {
        if (e.entityUuid !== job.uuid || e.details.oneofKind !== "income") continue;
        const { groupRrspContribution: you, groupRrspEmployerMatch: employer } = e.details.income;
        if (!you && !employer) continue;
        const y = e.date!.year;
        const row = byYear.get(y) ?? { year: y, you: 0, employer: 0 };
        row.you += you;
        row.employer += employer;
        byYear.set(y, row);
      }
      const found = [...byYear.keys()];
      const years = found.length
        ? Array.from({ length: Math.max(...found) - Math.min(...found) + 1 }, (_, i) => {
            const y = Math.min(...found) + i;
            return byYear.get(y) ?? { year: y, you: 0, employer: 0 };
          })
        : [];
      const currentRate = current?.rate ?? offer.employeeContributionRate;
      const changes: GroupRrspMatch["changes"] = [];
      let rate = currentRate;
      for (const s of current ? sets.slice(sets.indexOf(current) + 1) : sets) {
        if (Math.abs(s.rate - rate) < 1e-9) continue;
        changes.push({ year: s.date.year, rate: s.rate });
        rate = s.rate;
      }
      return [
        {
          jobUuid: job.uuid,
          firstName: p.givenName,
          employer: job.employer,
          matchingRate: offer.employerMatchingRate,
          capRate: offer.employerMatchCapRate,
          currentRate,
          startsIn: sets.find((s) => s.rate > 0)?.date.year,
          changes,
          years,
          totalYou: years.reduce((s, r) => s + r.you, 0),
          totalEmployer: years.reduce((s, r) => s + r.employer, 0),
        },
      ];
    }),
  );
}
